import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BrowserRouter,
  Link,
  NavLink,
  Navigate,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import { LogOut, Menu, ShoppingBag, X } from 'lucide-react';
import { LanguageSelector } from './components/LanguageSelector';
import { CurrencySelector } from './components/CurrencySelector';
import { HomePage } from './pages/HomePage';
import { ProductDetailPage } from './pages/ProductDetailPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { FAQPage } from './pages/FAQPage';
import { AboutPage } from './pages/AboutPage';
import { ContactPage } from './pages/ContactPage';
import { TermsPage } from './pages/TermsPage';
import { MentionsLegalesPage } from './pages/MentionsLegalesPage';
import { CGVPage } from './pages/CGVPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { AdminPage } from './pages/AdminPage';
import { AdminAppointmentsPage } from './pages/AdminAppointmentsPage';
import { AdminCalendarPage } from './pages/AdminCalendarPage';
import { AdminProductsPage } from './pages/AdminProductsPage';
import { AdminAnalyticsPage } from './pages/AdminAnalyticsPage';
import { LoginPage } from './pages/LoginPage';
import { CartPage } from './pages/CartPage';
import { CheckoutPage } from './pages/CheckoutPage';
import { UnderConstructionPage } from './pages/UnderConstructionPage';
import { Footer } from './components/Footer';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { ProtectedRoute } from './auth/ProtectedRoute';
import { UIProvider } from './ui/UIContext';
import { CartProvider, useCart } from './cart/CartContext';
import { CurrencyProvider } from './currency/CurrencyContext';
import { HelmetProvider } from 'react-helmet-async';
import { ThemePicker } from './themes/ThemePicker';
import { WhatsAppButton } from './components/WhatsAppButton';
import { OrganizationJsonLd } from './seo/OrganizationJsonLd';

const PREVIEW_STORAGE_KEY = 'handcrafted.preview-key';

// In-site cart/checkout is gated behind VITE_ECOMMERCE_ENABLED. When the
// seller has no commercial registration yet, the cart UI is hidden and
// /cart + /checkout routes redirect home — ready-to-wear pieces are
// reserved off-platform via WhatsApp instead.
export const ECOMMERCE_ENABLED =
  import.meta.env.VITE_ECOMMERCE_ENABLED === 'true';

/**
 * Reads `?preview=<KEY>` once, stashes a matching key in localStorage so the
 * bypass survives reloads, and strips the param from the URL bar. Returns
 * whether the current session has a valid bypass key cached.
 *
 * The bypass key is set at build time via VITE_UNDER_CONSTRUCTION_BYPASS_KEY
 * — only the admin knows it, and visitors can't guess it just by typing
 * `?preview=` blindly. Keep the value out of public docs.
 */
function useUnderConstructionBypass(): boolean {
  const location = useLocation();
  const expected = import.meta.env.VITE_UNDER_CONSTRUCTION_BYPASS_KEY as
    | string
    | undefined;

  const [bypassed, setBypassed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    if (!expected) return false;
    return window.localStorage.getItem(PREVIEW_STORAGE_KEY) === expected;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !expected) return;
    const params = new URLSearchParams(location.search);
    const incoming = params.get('preview');
    if (incoming && incoming === expected) {
      try {
        window.localStorage.setItem(PREVIEW_STORAGE_KEY, incoming);
      } catch {
        // localStorage may be unavailable (Safari private mode, full quota);
        // bypass still applies for the rest of this session via state.
      }
      setBypassed(true);
      params.delete('preview');
      const next = params.toString();
      window.history.replaceState(
        {},
        '',
        `${location.pathname}${next ? `?${next}` : ''}${location.hash}`,
      );
    }
  }, [location.search, location.pathname, location.hash, expected]);

  return bypassed;
}

function CartLink({ onNavigate }: { onNavigate?: () => void }) {
  const { t } = useTranslation();
  const { itemCount } = useCart();
  return (
    <NavLink
      to="/cart"
      onClick={onNavigate}
      className="relative inline-flex items-center gap-2 editorial-label text-ink-soft hover:text-ink transition-colors"
      title={t('nav.cart')}
    >
      <ShoppingBag className="w-4 h-4" />
      <span>{t('nav.cart')}</span>
      {itemCount > 0 && (
        <span
          aria-label={t('cart.itemCount', { count: itemCount })}
          className="absolute -top-2 -end-3 min-w-[18px] h-[18px] px-1 rounded-full bg-brass text-paper text-[10px] font-semibold flex items-center justify-center"
        >
          {itemCount}
        </span>
      )}
    </NavLink>
  );
}

function HeaderNav({
  mobile = false,
  onNavigate,
}: {
  mobile?: boolean;
  onNavigate?: () => void;
}) {
  const { t } = useTranslation();
  const { isAuthenticated, user, logout } = useAuth();

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `editorial-label transition-colors ${
      isActive ? 'text-brass' : 'text-ink-soft hover:text-ink'
    }`;

  const handleLogout = () => {
    logout();
    onNavigate?.();
  };

  return (
    <nav
      className={
        mobile ? 'flex flex-col items-start gap-5' : 'flex items-center gap-6'
      }
    >
      <NavLink to="/" end className={navLinkClass} onClick={onNavigate}>
        {t('nav.home')}
      </NavLink>
      {isAuthenticated && user?.role === 'ADMIN' && (
        <>
          <NavLink to="/admin" end className={navLinkClass} onClick={onNavigate}>
            {t('nav.dashboard')}
          </NavLink>
          <NavLink to="/admin/appointments" className={navLinkClass} onClick={onNavigate}>
            {t('nav.appointments')}
          </NavLink>
          <NavLink to="/admin/calendar" className={navLinkClass} onClick={onNavigate}>
            {t('nav.calendar')}
          </NavLink>
          <NavLink to="/admin/products" className={navLinkClass} onClick={onNavigate}>
            {t('nav.products')}
          </NavLink>
          <NavLink to="/admin/analytics" className={navLinkClass} onClick={onNavigate}>
            {t('nav.analytics')}
          </NavLink>
        </>
      )}
      {ECOMMERCE_ENABLED && <CartLink onNavigate={onNavigate} />}
      {isAuthenticated ? (
        <button
          onClick={handleLogout}
          className="editorial-label text-ink-soft hover:text-ink transition-colors inline-flex items-center gap-2"
          title={t('nav.logout')}
        >
          <LogOut className="w-3.5 h-3.5" />
          <span>{t('nav.logout')}</span>
        </button>
      ) : (
        <NavLink to="/login" className={navLinkClass} onClick={onNavigate}>
          {t('nav.login')}
        </NavLink>
      )}
    </nav>
  );
}

function AppShell() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const bypassed = useUnderConstructionBypass();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    document.documentElement.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  // Close the mobile menu whenever the route changes so it never lingers
  // open over a freshly navigated page.
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Soft-launch gate. Public visitors see the under-construction page;
  // admin login + admin dashboard routes stay accessible so the seller
  // can keep managing the catalogue from production. Devs / the admin can
  // also bypass with ?preview=<KEY> (key set at build time).
  const underConstructionEnabled =
    import.meta.env.VITE_UNDER_CONSTRUCTION === 'true';
  const isAdminPath =
    location.pathname.startsWith('/admin') || location.pathname === '/login';
  const showUnderConstruction =
    underConstructionEnabled && !bypassed && !isAdminPath;

  if (showUnderConstruction) {
    return <UnderConstructionPage />;
  }

  return (
    <div className={`min-h-screen bg-ivory flex flex-col ${i18n.language === 'ar' ? 'font-arabic' : ''}`}>
      <header className="border-b border-linen bg-paper/60 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-editorial mx-auto px-6 sm:px-10 py-5">
          <div className="flex items-center justify-between gap-8">
            <Link to="/" className="group flex items-baseline gap-3">
              <span className="editorial-number text-2xl leading-none">N°</span>
              <span className="font-display text-2xl tracking-wide leading-none">
                {t('header.title')}
              </span>
            </Link>
            <div className="hidden md:flex items-center gap-10">
              <HeaderNav />
              <div className="w-px h-5 bg-linen" />
              <LanguageSelector />
              <CurrencySelector />
            </div>
            {/* Mobile hamburger — only below md where the inline nav is hidden */}
            <button
              type="button"
              className="md:hidden inline-flex items-center justify-center w-10 h-10 -me-2 text-ink"
              onClick={() => setMobileMenuOpen((v) => !v)}
              aria-label={t(mobileMenuOpen ? 'nav.closeMenu' : 'nav.openMenu')}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-menu"
            >
              {mobileMenuOpen ? (
                <X className="w-5 h-5" aria-hidden="true" />
              ) : (
                <Menu className="w-5 h-5" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu panel — collapses the nav + language selector that the
            desktop bar hides below md. Conditionally rendered so it stays out
            of the DOM (and out of test queries) when closed. */}
        {mobileMenuOpen && (
          <div
            id="mobile-menu"
            className="md:hidden border-t border-linen bg-paper"
          >
            <div className="max-w-editorial mx-auto px-6 py-6 flex flex-col gap-6">
              <HeaderNav mobile onNavigate={() => setMobileMenuOpen(false)} />
              <div className="h-px bg-linen" />
              <LanguageSelector />
              <CurrencySelector />
            </div>
          </div>
        )}
      </header>

      <main className="flex-1 w-full">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/products/:id" element={<ProductDetailPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
          <Route path="/cgv" element={<CGVPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/cart"
            element={ECOMMERCE_ENABLED ? <CartPage /> : <Navigate to="/" replace />}
          />
          <Route
            path="/checkout"
            element={ECOMMERCE_ENABLED ? <CheckoutPage /> : <Navigate to="/" replace />}
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute role="ADMIN">
                <AdminPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/appointments"
            element={
              <ProtectedRoute role="ADMIN">
                <AdminAppointmentsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/calendar"
            element={
              <ProtectedRoute role="ADMIN">
                <AdminCalendarPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/products"
            element={
              <ProtectedRoute role="ADMIN">
                <AdminProductsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/analytics"
            element={
              <ProtectedRoute role="ADMIN">
                <AdminAnalyticsPage />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
      <Footer />
      <WhatsAppButton />
      <ThemePicker />
      <OrganizationJsonLd />
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <UIProvider>
            <CurrencyProvider>
              <CartProvider>
                <AppShell />
              </CartProvider>
            </CurrencyProvider>
          </UIProvider>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
